// src/components/admin/villa/VillaTable.jsx
import React from 'react';
import { formatCurrency } from '../../../utils/formatters';
import Button from '../../common/Button';
import Pagination from '../../common/Pagination';
import Loading from '../../common/Loading';
import { FaEdit, FaTrash, FaStar, FaRegStar, FaEye, FaImage } from 'react-icons/fa';
import Swal from 'sweetalert2';

const VillaTable = ({
  properties,
  categories,
  locations,
  onEdit,
  onDelete,
  onToggleFeatured,
  onStatusChange,
  loading,
  currentPage,
  totalPages,
  totalItems, // ✅ TAMBAH: terima prop totalItems
  onPageChange
}) => {
  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : '-';
  };

  const getLocationName = (locationId) => {
    const location = locations.find(l => l.id === locationId);
    return location ? location.name : '-';
  };

  const formatPrice = (priceIdr, priceUsd) => {
    if (priceIdr && parseFloat(priceIdr) > 0) {
      return `Rp ${parseFloat(priceIdr).toLocaleString('id-ID')}`;
    }
    if (priceUsd && parseFloat(priceUsd) > 0) {
      return `$${parseFloat(priceUsd).toLocaleString('en-US')}`;
    }
    return 'Contact Us';
  };

  const getMainPrice = (property) => {
    if (property.daily_price_idr && parseFloat(property.daily_price_idr) > 0) {
      return { 
        price: formatPrice(property.daily_price_idr, property.daily_price_usd),
        period: '/day'
      };
    }
    if (property.monthly_price_idr && parseFloat(property.monthly_price_idr) > 0) {
      return { 
        price: formatPrice(property.monthly_price_idr, property.monthly_price_usd),
        period: '/month'
      };
    }
    if (property.yearly_price_idr && parseFloat(property.yearly_price_idr) > 0) {
      return { 
        price: formatPrice(property.yearly_price_idr, property.yearly_price_usd),
        period: '/year'
      };
    }
    if (property.sale_price_idr && parseFloat(property.sale_price_idr) > 0) {
      return { 
        price: formatPrice(property.sale_price_idr, property.sale_price_usd),
        period: ''
      };
    }
    return { price: 'Contact Us', period: '' };
  };

  const getPropertyImage = (property) => {
    if (property.primary_image) {
      return property.primary_image;
    }
    if (property.images && property.images.length > 0) {
      const primaryImage = property.images.find(img => img.is_primary === 1);
      if (primaryImage) return primaryImage.image_path;
      return property.images[0].image_path;
    }
    return null;
  };

  // Handle delete with confirmation
  const handleDeleteClick = async (property) => {
    try {
      const result = await Swal.fire({
        title: 'Delete Property?',
        html: `Are you sure you want to delete <strong>"${property.title}"</strong>?<br><br><small class="text-gray-600">This action cannot be undone.</small>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Yes, Delete',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-xl font-bold',
          confirmButton: 'px-6 py-3 rounded-xl font-semibold',
          cancelButton: 'px-6 py-3 rounded-xl font-semibold'
        }
      });

      if (result.isConfirmed) {
        Swal.fire({
          title: 'Deleting...',
          html: 'Please wait a moment',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        await onDelete(property.id, property.title);
        
        await Swal.fire({
          title: 'Deleted!',
          html: `<strong>"${property.title}"</strong> has been successfully deleted.`,
          icon: 'success',
          confirmButtonColor: '#10b981',
          confirmButtonText: 'OK',
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: 'rounded-2xl',
            title: 'text-xl font-bold',
            confirmButton: 'px-6 py-3 rounded-xl font-semibold'
          }
        });
      }
    } catch (error) {
      console.error('Error in delete process:', error);
      Swal.close();
      
      await Swal.fire({
        title: 'Error',
        text: 'Failed to delete property. Please try again.',
        icon: 'error',
        confirmButtonColor: '#ef4444',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-xl font-bold',
          confirmButton: 'px-6 py-3 rounded-xl font-semibold'
        }
      });
    }
  };

  // Handle featured toggle
  const handleFeatureToggle = async (property) => {
    try {
      const isCurrentlyFeatured = property.featured === 1;
      const action = isCurrentlyFeatured ? 'remove from' : 'add to';
      
      const result = await Swal.fire({
        title: 'Featured Status',
        html: `Are you sure you want to ${action} featured for <strong>"${property.title}"</strong>?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#f59e0b',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Yes, Continue',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-xl font-bold',
          confirmButton: 'px-6 py-3 rounded-xl font-semibold',
          cancelButton: 'px-6 py-3 rounded-xl font-semibold'
        }
      });

      if (result.isConfirmed) {
        Swal.fire({
          title: 'Updating...',
          html: 'Please wait a moment',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        await onToggleFeatured(property.id, isCurrentlyFeatured, property.title);
        
        const successMessage = isCurrentlyFeatured 
          ? `<strong>"${property.title}"</strong> removed from featured list` 
          : `<strong>"${property.title}"</strong> added to featured list`;
        
        await Swal.fire({
          title: 'Success!',
          html: successMessage,
          icon: 'success',
          confirmButtonColor: '#10b981',
          confirmButtonText: 'OK',
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: 'rounded-2xl',
            title: 'text-xl font-bold',
            confirmButton: 'px-6 py-3 rounded-xl font-semibold'
          }
        });
      }
    } catch (error) {
      console.error('Error toggling featured:', error);
      Swal.close();
      
      await Swal.fire({
        title: 'Error',
        text: 'Failed to change featured status. Please try again.',
        icon: 'error',
        confirmButtonColor: '#ef4444',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-xl font-bold',
          confirmButton: 'px-6 py-3 rounded-xl font-semibold'
        }
      });
    }
  };

  // Handle status change
  const handleStatusChange = async (property, newStatus) => {
    try {
      Swal.fire({
        title: 'Updating Status...',
        html: 'Please wait a moment',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      await onStatusChange(property.id, newStatus, property.title);
      
      const statusText = newStatus === 'active' ? 'Active' : 'Inactive';
      await Swal.fire({
        title: 'Status Updated!',
        html: `<strong>"${property.title}"</strong> status changed to <strong>${statusText}</strong>`,
        icon: 'success',
        confirmButtonColor: '#10b981',
        confirmButtonText: 'OK',
        timer: 3000,
        timerProgressBar: true,
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-xl font-bold',
          confirmButton: 'px-6 py-3 rounded-xl font-semibold'
        }
      });
    } catch (error) {
      console.error('Error changing status:', error);
      Swal.close();
      
      await Swal.fire({
        title: 'Error',
        text: 'Failed to change property status. Please try again.',
        icon: 'error',
        confirmButtonColor: '#ef4444',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-xl font-bold',
          confirmButton: 'px-6 py-3 rounded-xl font-semibold'
        }
      });
    }
  };

  const getRowNumber = (index) => {
    const itemsPerPage = 10;
    return ((currentPage - 1) * itemsPerPage) + index + 1;
  };

  if (loading) return <Loading />;

  return (
    <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50 overflow-hidden">
      {/* Summary Info */}
      <div className="px-6 py-4 bg-gradient-to-r from-amber-50 via-amber-50/50 to-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-amber-100 rounded-lg">
              <FaImage className="text-amber-600" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">Total Properties</h3>
              <p className="text-xs text-gray-600">Showing {properties.length} villa(s)</p>
            </div>
          </div>
          {totalPages > 0 && (
            <div className="text-sm font-medium text-gray-700">
              Page {currentPage} of {totalPages}
            </div>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gradient-to-r from-amber-50 via-amber-50/50 to-gray-50">
            <tr>
              <th className="px-4 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-16">
                No
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                Property
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                Category
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                Location
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                Price
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                Specifications
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {properties.map((property, index) => {
              const imageUrl = getPropertyImage(property);
              const priceInfo = getMainPrice(property);
              const rowNumber = getRowNumber(index);
              
              return (
                <tr key={property.id} className="hover:bg-amber-50/30 transition-colors duration-150">
                  <td className="px-4 py-4 text-center">
                    <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 text-amber-800 font-bold text-sm shadow-sm">
                      {rowNumber}
                    </div>
                  </td>

                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-16 w-16">
                        {imageUrl ? (
                          <img
                            className="h-16 w-16 rounded-xl object-cover shadow-sm border border-gray-200"
                            src={imageUrl}
                            alt={property.title}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div 
                          className={`h-16 w-16 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center shadow-sm border border-gray-200 ${imageUrl ? 'hidden' : 'flex'}`}
                        >
                          <FaImage className="text-gray-400" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-semibold text-gray-900">
                          {property.title}
                        </div>
                        <div className="text-sm text-gray-600">
                          {property.description?.substring(0, 60)}
                          {property.description?.length > 60 ? '...' : ''}
                        </div>
                        <div className="mt-1">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            property.property_status === 'leasehold' 
                              ? 'bg-amber-100 text-amber-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {property.property_status === 'leasehold' ? 'Leasehold' : 'Freehold'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900">
                      {property.category_name || getCategoryName(property.category_id)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900">
                      {property.location_name || getLocationName(property.location_id)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-bold text-gray-900">{priceInfo.price}</div>
                    {priceInfo.period && (
                      <div className="text-xs text-amber-600 font-medium">{priceInfo.period}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1 text-sm text-gray-700">
                      <div className="font-medium">
                        {property.bedrooms} BD • {property.bathrooms} BA
                      </div>
                      <div className="text-xs text-gray-600">
                        {property.max_guests} Guests
                      </div>
                      <div className="text-xs text-gray-600">
                        {property.property_size_sqm} m² • {property.land_size_sqm} m²
                      </div>
                      {property.view_type && (
                        <div className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          {property.view_type}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col space-y-2">
                      <select
                        value={property.status}
                        onChange={(e) => handleStatusChange(property, e.target.value)}
                        className="text-xs font-medium border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white transition-all shadow-sm hover:border-amber-400"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                      {property.featured === 1 && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 border border-amber-200 shadow-sm">
                          <FaStar className="mr-1.5" />
                          Featured
                        </span>
                      )}
                      {property.pets_allowed === 1 && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold bg-green-100 text-green-800 border border-green-200 shadow-sm">
                          Pet Friendly
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center justify-center gap-2">
                      {/* Edit Button */}
                      <button
                        onClick={() => onEdit(property)}
                        className="inline-flex items-center justify-center p-2.5 border border-amber-300 shadow-sm text-sm font-semibold rounded-lg text-amber-700 bg-amber-50 hover:bg-amber-100 hover:border-amber-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-all duration-200 transform hover:scale-110 active:scale-95"
                        title="Edit Property"
                      >
                        <FaEdit className="h-4 w-4" />
                      </button>

                      {/* Featured Toggle Button */}
                      <button
                        onClick={() => handleFeatureToggle(property)}
                        className={`inline-flex items-center justify-center p-2.5 border shadow-sm text-sm font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 transform hover:scale-110 active:scale-95 ${
                          property.featured === 1
                            ? 'border-yellow-400 text-yellow-700 bg-yellow-50 hover:bg-yellow-100 hover:border-yellow-500 focus:ring-yellow-500'
                            : 'border-gray-300 text-gray-700 bg-gray-50 hover:bg-gray-100 hover:border-gray-400 focus:ring-gray-500'
                        }`}
                        title={property.featured === 1 ? 'Remove from Featured' : 'Add to Featured'}
                      >
                        {property.featured === 1 ? (
                          <FaStar className="h-4 w-4" />
                        ) : (
                          <FaRegStar className="h-4 w-4" />
                        )}
                      </button>

                      {/* Delete Button */}
                      <button
                        onClick={() => handleDeleteClick(property)}
                        className="inline-flex items-center justify-center p-2.5 border border-red-300 shadow-sm text-sm font-semibold rounded-lg text-red-700 bg-red-50 hover:bg-red-100 hover:border-red-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200 transform hover:scale-110 active:scale-95"
                        title="Delete Property"
                      >
                        <FaTrash className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {properties.length === 0 && (
        <div className="text-center py-16 bg-gradient-to-b from-gray-50 to-white">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 mb-4">
            <FaImage className="h-8 w-8 text-amber-600" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">No Properties Found</h3>
          <p className="text-sm text-gray-600 max-w-md mx-auto">
            Start by adding your first property to manage your villa listings.
          </p>
        </div>
      )}

      {properties.length > 0 && (
        <div className="px-6 py-5 border-t border-gray-200 bg-gradient-to-r from-gray-50 via-amber-50/20 to-gray-50">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages || 1}
            onPageChange={onPageChange}
            totalItems={totalItems}  // ✅ TAMBAH: teruskan totalItems ke Pagination
            itemsPerPage={10}
          />
        </div>
      )}
    </div>
  );
};

export default VillaTable;